import { getCometBrowserInfo, isCometBrowser, isCometMobileBrowser, } from '#pplx/frontend/comet/client/utils.js';
import { getSessionIdCookie, getVisitorIdCookie, } from '#pplx/frontend/cookies/sessionCookieManager.js';
import { EPPO_OPEN_FEATURE_DOMAIN } from '#pplx/frontend/experimentation/openfeature/openFeatureClient.js';
import { WEB_VERSION } from '#pplx/frontend/fetcher/constants.js';
import { getApiClient } from '#pplx/frontend/fetcher/utils.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useUserInfo } from '#pplx/frontend/user-info/useUserInfo.js';
import { useUserOrgInfo } from '#pplx/frontend/user-info/useUserOrgInfo.js';
import { OpenFeature } from '@openfeature/web-sdk';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
const USER_INFO_REASON = 'eppo-experiments-provider';
const ORG_INFO_REASON = 'eppo-experiments-provider';
const DEFAULT_EPPO_ORG_INFO = {
    orgServiceType: undefined,
};
let eppoContextUpdateChain = Promise.resolve();
export const mergeOpenFeatureContext = (context) => {
    const eppo = eppoContextUpdateChain.then(async () => {
        // OpenFeature replaces context, while the value groups here pass patches.
        const nextContext = {
            ...OpenFeature.getContext(EPPO_OPEN_FEATURE_DOMAIN),
            ...context,
        };
        await OpenFeature.setContext(EPPO_OPEN_FEATURE_DOMAIN, nextContext);
    });
    eppoContextUpdateChain = eppo.catch(() => undefined);
    return eppo;
};
export function extractEmailDomain(email) {
    if (!email || typeof email !== 'string') {
        return undefined;
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail === '') {
        return undefined;
    }
    const parts = trimmedEmail.split('@');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return undefined;
    }
    return parts[1];
}
function getIsEnterpriseFromOrgUUID(orgUUID) {
    return Boolean(orgUUID && orgUUID !== 'none');
}
function getPlatformFromWebDevice({ isIOS, isAndroid, }) {
    if (isIOS) {
        return 'ios';
    }
    if (isAndroid) {
        return 'android';
    }
    return 'web';
}
function getIsAppPath(pathname) {
    return pathname === '/app' || Boolean(pathname?.startsWith('/app/'));
}
function useTrackedMergeOpenFeatureContext(context) {
    const [appliedContext, setAppliedContext] = useState(null);
    useLayoutEffect(() => {
        let isCurrent = true;
        void mergeOpenFeatureContext(context)
            .then(() => {
            if (isCurrent) {
                setAppliedContext(context);
            }
        })
            .catch(() => undefined);
        return () => {
            isCurrent = false;
        };
    }, [context]);
    return appliedContext;
}
function useApplyBootstrapContext({ appClientVersion, cfCountry, deployName, effectivePhase, isApp, isComet, isLinuxOS, isMacOS, isMobileStyle, isWindowsApp, locale, platform, session, sessionId, visitorId, }) {
    const context = useMemo(() => {
        const isCometMobile = isCometMobileBrowser();
        const isCometDesktop = isComet && !isCometMobile;
        const cometDeviceId = getCometBrowserInfo()?.device_id ?? undefined;
        const userEmail = session?.user?.email ?? undefined;
        const userEmailDomain = extractEmailDomain(userEmail);
        const subscriptionTier = session?.user?.subscription_tier ?? undefined;
        const paymentTier = session?.user?.payment_tier ?? undefined;
        const subscriptionSource = session?.user?.subscription_source ?? undefined;
        const orgUUID = session?.user?.org_uuid ?? undefined;
        const segment = subscriptionSource === 'enterprise' ? 'enterprise' : 'consumer';
        const isEnterprise = getIsEnterpriseFromOrgUUID(orgUUID);
        const isPayingUser = paymentTier === 'paid' || paymentTier === 'discount';
        const isProMax = subscriptionTier === 'pro' || subscriptionTier === 'max';
        const isEnterprisePro = isEnterprise && subscriptionTier === 'pro';
        const isEnterpriseMax = isEnterprise && subscriptionTier === 'max';
        const cfLanguage = new Intl.Locale(locale).language;
        return {
            targetingKey: visitorId ?? sessionId,
            visitorId: visitorId ?? undefined,
            sessionId: sessionId ?? undefined,
            userEmail,
            userEmailDomain,
            userId: session?.user?.id ?? undefined,
            orgUUID,
            org_uuid: orgUUID,
            organization_uuid: orgUUID,
            org_id: orgUUID,
            appApiClient: getApiClient({ isMobile: isMobileStyle, isWindowsApp, isLinuxOS, isMacOS }),
            cfCountry,
            cfLanguage,
            language: cfLanguage,
            locale,
            isApp,
            platform,
            isComet,
            isCometDesktop,
            isCometMobile,
            isEnterprise,
            isEnterprisePro,
            isEnterpriseMax,
            isPayingUser,
            isProMax,
            deployName,
            appApiVersion: WEB_VERSION,
            ...(appClientVersion ? { appClientVersion } : {}),
            cometDeviceId,
            subscription_tier: subscriptionTier,
            subscriptionTier,
            subscription_status: session?.user?.subscription_status ?? 'none',
            subscriptionStatus: session?.user?.subscription_status ?? 'none',
            subscriptionSource,
            payment_tier: paymentTier,
            paymentTier,
            segment,
            effectivePhase,
        };
    }, [
        appClientVersion,
        cfCountry,
        deployName,
        effectivePhase,
        isApp,
        isComet,
        isLinuxOS,
        isMacOS,
        isMobileStyle,
        isWindowsApp,
        locale,
        platform,
        session,
        sessionId,
        visitorId,
    ]);
    const appliedContext = useTrackedMergeOpenFeatureContext(context);
    return appliedContext?.userId;
}
function useApplyUserInfoContext({ cfCountry }) {
    const userInfoResult = useUserInfo({ reason: USER_INFO_REASON });
    const userInfo = useMemo(() => ({
        isStudent: userInfoResult.isStudent,
        studentCountry: userInfoResult.studentCountry,
        syntheticUserType: userInfoResult.syntheticUserType,
    }), [userInfoResult.isStudent, userInfoResult.studentCountry, userInfoResult.syntheticUserType]);
    const context = useMemo(() => ({
        country: userInfo.studentCountry ?? cfCountry,
        isStudent: userInfo.isStudent,
        syntheticUserType: userInfo.syntheticUserType,
    }), [cfCountry, userInfo]);
    const appliedContext = useTrackedMergeOpenFeatureContext(context);
    const hasFetchedUserInfo = userInfoResult.query.isFetched || userInfoResult.query.isError;
    return {
        hasLoadedUserInfo: hasFetchedUserInfo && appliedContext === context,
    };
}
function useApplyOrgInfoContext({ session }) {
    const orgUUID = session?.user?.org_uuid ?? undefined;
    const shouldFetchOrgInfo = getIsEnterpriseFromOrgUUID(orgUUID);
    const orgInfoResult = useUserOrgInfo({
        enabled: shouldFetchOrgInfo,
        reason: ORG_INFO_REASON,
    });
    const orgInfo = useMemo(() => shouldFetchOrgInfo ? { orgServiceType: orgInfoResult.orgServiceType } : DEFAULT_EPPO_ORG_INFO, [orgInfoResult.orgServiceType, shouldFetchOrgInfo]);
    const context = useMemo(() => ({
        org_service_type: orgInfo.orgServiceType,
    }), [orgInfo.orgServiceType]);
    const appliedContext = useTrackedMergeOpenFeatureContext(context);
    const hasFetchedOrgInfo = !shouldFetchOrgInfo || orgInfoResult.query.isFetched || orgInfoResult.query.isError;
    return {
        hasLoadedOrgInfo: !shouldFetchOrgInfo || (hasFetchedOrgInfo && appliedContext === context),
    };
}
export function useOpenFeatureContext({ appClientVersion, cfCountry, effectivePhase, session, }) {
    const visitorId = getVisitorIdCookie();
    const sessionId = getSessionIdCookie();
    const isComet = isCometBrowser();
    const { env: { deployName }, device: { isWindowsApp, isLinuxOS, isMacOS, isIOS, isAndroid }, } = useWebPlatform();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const mobileClient = searchParams?.get('mobile.client') ?? undefined;
    const platform = mobileClient ?? getPlatformFromWebDevice({ isIOS, isAndroid });
    const isApp = getIsAppPath(pathname);
    const { locale } = useIntl();
    const { isMobileStyle } = useIsMobile();
    const appliedEppoUserId = useApplyBootstrapContext({
        appClientVersion,
        cfCountry,
        deployName,
        effectivePhase,
        isApp,
        isComet,
        isLinuxOS,
        isMacOS,
        isMobileStyle,
        isWindowsApp,
        locale,
        platform,
        session,
        sessionId,
        visitorId,
    });
    const { hasLoadedUserInfo } = useApplyUserInfoContext({
        cfCountry,
    });
    const { hasLoadedOrgInfo } = useApplyOrgInfoContext({
        session,
    });
    return {
        appliedEppoUserId,
        hasLoadedUserInfo: hasLoadedUserInfo && hasLoadedOrgInfo,
    };
}
//# sourceMappingURL=openFeatureContext.js.map